"use client";

import { useState } from "react";
import type { ConciergeProposal, GuidedIssue } from "../lib/concierge";
import type { MelodyTapAnalysis } from "../lib/melody-tap";
import type { GridValue } from "../lib/music";
import { MelodyTapGuide } from "./MelodyTapGuide";

type Props = {
  open: boolean;
  phase: "before" | "after";
  bpm: number;
  grid: GridValue;
  tapCount: number;
  count: number | null;
  proposal: ConciergeProposal | null;
  beforeCount: number | null;
  afterCount: number;
  melodyTapRecommendationApplied: boolean;
  onClose: () => void;
  onChooseTempo: (bpm: number) => void;
  onGridChange: (grid: GridValue) => void;
  onTap: () => void;
  onIssue: (issue: GuidedIssue) => void;
  onAnalyze: () => void;
  onAdopt: () => void;
  onRevert: () => void;
  onStartMelodyTapAudio: () => Promise<boolean>;
  onStopMelodyTapAudio: () => void;
  onApplyMelodyTap: (analysis: MelodyTapAnalysis) => void;
  onRestoreMelodyTap: () => void;
};

const ISSUES: Array<{ issue: GuidedIssue; label: string }> = [
  { issue: "missing-detail", label: "細かい音が抜けた" },
  { issue: "too-many", label: "余計な細かい音が多い" },
  { issue: "semitone", label: "隣の半音へずれる" },
  { issue: "split-notes", label: "1音が途中で分かれすぎる" },
  { issue: "merged-notes", label: "音が1つにまとまりすぎる" },
  { issue: "timing", label: "リズム位置が合わない" },
];

export function ConciergePanel(props: Props) {
  const [melodyTapOpen, setMelodyTapOpen] = useState(false);
  if (!props.open) return null;
  return (
    <>
      <button type="button" className="concierge-backdrop" aria-label="調整アシスタントを閉じる" onClick={props.onClose} />
      <aside className="concierge-panel" role="dialog" aria-modal="true" aria-label="録音とMIDI変換の調整アシスタント">
        <header><div><span>調整アシスタント</span><strong>{props.phase === "before" ? melodyTapOpen ? "メロディの譜割りをタップ" : "録音前に曲の速さを確認" : "結果の困りごとを教えてください"}</strong></div><button type="button" onClick={props.onClose} aria-label="閉じる">×</button></header>
        {props.phase === "before" ? melodyTapOpen ? (
          <MelodyTapGuide
            bpm={props.bpm}
            count={props.count}
            bpmConfirmed={props.tapCount > 1 || props.bpm !== 100}
            recommendationApplied={props.melodyTapRecommendationApplied}
            onStartAudio={props.onStartMelodyTapAudio}
            onStopAudio={props.onStopMelodyTapAudio}
            onApply={props.onApplyMelodyTap}
            onRestore={props.onRestoreMelodyTap}
            onBack={() => { props.onStopMelodyTapAudio(); setMelodyTapOpen(false); }}
          />
        ) : (
          <div className="concierge-body">
            <p>① 曲の速さを確認し、② 必要なら歌う予定のメロディをタップして、MIDIの細かさを調べます。どちらも毎回必須ではありません。</p>
            <div className="capture-method-explainer"><span><strong>自動で録る</strong><small>歌声から音の高さと区切りを自動で判断</small></span><span><strong>タップしながら録る</strong><small>音が変わるたび押して、自分で区切りを教える</small></span></div>
            <div className="concierge-tempo"><button onClick={() => props.onChooseTempo(70)}>ゆっくり<small>70 BPM</small></button><button onClick={() => props.onChooseTempo(100)}>普通<small>100 BPM</small></button><button onClick={() => props.onChooseTempo(140)}>速い<small>140 BPM</small></button></div>
            <div className="concierge-current"><span>① 現在の速さ</span><strong>{props.bpm} BPM</strong><button onClick={props.onTap}>BPM TAP<small>{props.tapCount > 1 ? "測定中" : "拍をタップ"}</small></button></div>
            <button className="melody-tap-launch" onClick={() => setMelodyTapOpen(true)}><span>② メロディの細かさを確認</span><strong>メロディをタップして調べる</strong><small>音が変わるたびにTAP · 伸ばす音はHOLD</small></button>
            <div className="concierge-grid"><span>現在のGRID</span>{(["1/8", "1/16", "1/32"] as GridValue[]).map((value) => <button key={value} className={props.grid === value ? "active" : ""} onClick={() => props.onGridChange(value)}>{value}</button>)}</div>
            <small>マイクから音の高さはGRIDより細かく測ります。MELODY TAPは音の高さを決めず、音の開始・長さ・必要な時間分解能だけを見ます。</small>
            <button className="concierge-primary" onClick={props.onClose}>この設定で録音へ</button>
          </div>
        ) : (
          <div className="concierge-body">
            {props.beforeCount === null ? <>
              <p>専門用語は不要です。近いものを1つ選ぶと、アプリが設定候補へ翻訳します。</p>
              <div className="concierge-issues">{ISSUES.map((item) => <button key={item.issue} onClick={() => props.onIssue(item.issue)}>{item.label}</button>)}</div>
            </> : <div className="concierge-compare"><span>BEFORE<strong>{props.beforeCount} notes</strong></span><i>→</i><span>AFTER<strong>{props.afterCount} notes</strong></span><p>同じ歌声の測定データを使って再解析しました。良ければ採用、違えば元へ戻せます。</p><div><button onClick={props.onRevert}>元の設定へ戻す</button><button className="concierge-primary" onClick={props.onAdopt}>この結果を採用</button></div></div>}
            {props.beforeCount === null && props.proposal && <div className="concierge-proposal"><span>提案</span><strong>{props.proposal.title}</strong><p>{props.proposal.explanation}</p>{props.proposal.needsTempoReview && <><div className="concierge-current"><span>現在</span><strong>{props.bpm} BPM</strong><button onClick={props.onTap}>BPM TAP<small>拍をタップ</small></button></div><div className="concierge-grid"><span>GRIDを確認</span>{(["1/8", "1/16", "1/32"] as GridValue[]).map((value) => <button key={value} className={props.grid === value ? "active" : ""} onClick={() => props.onGridChange(value)}>{value}</button>)}</div></>}<button className="concierge-primary" onClick={props.onAnalyze}>この設定で再解析</button></div>}
          </div>
        )}
      </aside>
    </>
  );
}
